import {
  localIsoDate,
  termsContaining,
  type TermFilter,
  type TermLookup,
} from "./terms.js";

/** Honors is app-owned: SIS imports must never replace this semester document. */
export interface HonorsEvent {
  on: boolean;
  /** UTC milliseconds. */
  at: number;
  by: string;
}

export type HonorsTerm = string;

/** Full-year sections participate in each shorter term; use FY itself only
 * when the school's current sections have no shorter terms. */
export function honorsTerms(
  sections: readonly { term: string }[],
): HonorsTerm[] {
  const terms = [
    ...new Set(sections.map((section) => section.term.trim()).filter(Boolean)),
  ];
  const shorter = terms.filter((term) => term !== "FY");
  return (shorter.length ? shorter : terms).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
}

/**
 * The term a school is most likely in right now.
 *
 * This is a calendar approximation, not a fact. It spreads the term list evenly
 * across a September-to-June instructional year, which lands the right side of
 * a semester boundary except for the week or two around it, and assumes the
 * list is in chronological order (honorsTerms sorts numerically, so S1/S2 and
 * T1/T2/T3 are). Use it to pick an opening tab, never to decide anything that
 * has to be correct -- whoever is looking can always pick another term.
 *
 * **Prefer `preferredHonorsTerm`.** This function's original rationale was that
 * "the SIS import gives us term codes and never the dates they run", and that
 * was simply wrong: `marking_periods.csv` carries start and end dates and has
 * been imported all along, just never made readable. This remains the fallback
 * for a school whose export does not deliver that file.
 */
export function currentHonorsTerm(
  terms: readonly HonorsTerm[],
  today: Date = new Date(),
): HonorsTerm {
  if (terms.length <= 1) return terms[0] ?? "";
  // September is month 0 of the school year; July and August belong to the year
  // that is starting, so they wrap back to the front rather than the end.
  const monthsIn = (today.getMonth() - 8 + 12) % 12;
  const instructional = monthsIn > 9 ? 0 : monthsIn;
  const index = Math.floor((instructional * terms.length) / 10);
  return terms[Math.min(index, terms.length - 1)];
}

/**
 * The honors term to open on: the real one when term dates are available,
 * otherwise the calendar guess.
 *
 * Walks the terms covering today narrowest-first and takes the first whose code
 * appears in the honors term list. Narrowest-first matters because a school runs
 * several term schemes at once -- advisory on FY, courses on S1/S2, MS specials
 * on quarters -- and `honorsTerms` has already dropped FY when shorter terms
 * exist, so the first match is the shortest term honors actually tracks.
 *
 * Falls back to `currentHonorsTerm` whenever term dates are missing, do not
 * cover today (summer), or name terms honors does not use. That fallback is not
 * a failure path: it is the normal answer at a school whose export does not
 * deliver `marking_periods.csv`.
 */
export function preferredHonorsTerm(
  terms: readonly HonorsTerm[],
  lookup: TermLookup | undefined,
  today: Date = new Date(),
  filter: TermFilter = {},
): HonorsTerm {
  if (lookup) {
    const known = new Map(
      terms.map((term) => [term.trim().toLowerCase(), term]),
    );
    for (const term of termsContaining(lookup, localIsoDate(today), filter)) {
      const match =
        known.get(term.abbreviation?.trim().toLowerCase() ?? "") ??
        known.get(term.marking_period_id?.trim().toLowerCase() ?? "") ??
        known.get(term.name?.trim().toLowerCase() ?? "");
      if (match) return match;
    }
  }
  return currentHonorsTerm(terms, today);
}

export interface HonorsRecord {
  school_id: "HS";
  school_year: string;
  term: HonorsTerm;
  /** section id -> student id -> chronological changes; absent means unchecked. */
  sections: Record<string, Record<string, HonorsEvent[]>>;
}

export interface HonorsChange {
  student_id: string;
  on: boolean;
  /** History length seen by the editor, for detecting conflicting edits. */
  expected_version: number;
}

export function emptyHonorsRecord(
  schoolYear: string,
  term: HonorsTerm,
): HonorsRecord {
  return { school_id: "HS", school_year: schoolYear, term, sections: {} };
}

export function doingHonors(events: readonly HonorsEvent[] = []): boolean {
  return events[events.length - 1]?.on ?? false;
}

/** Apply only intentional edits to a fresh transaction snapshot. */
export function applyHonorsChanges(
  current: HonorsRecord,
  sectionId: string,
  changes: readonly HonorsChange[],
  actor: string,
  at: number,
): HonorsRecord {
  const section = { ...current.sections[sectionId] };
  for (const change of changes) {
    const events = section[change.student_id] ?? [];
    // A colleague may already have made the same change: no duplicate event.
    if (doingHonors(events) === change.on) continue;
    if (events.length !== change.expected_version) {
      throw new Error(
        "Another person changed honors for one of these students. Discard your draft and refresh before trying again.",
      );
    }
    section[change.student_id] = [...events, { on: change.on, at, by: actor }];
  }
  return {
    ...current,
    sections: { ...current.sections, [sectionId]: section },
  };
}

/** Firestore storage formula for these maps, arrays, strings, booleans and numbers.
 * Includes a conservative allowance for the document path. Not JSON length:
 * Firestore charges 32 bytes per map and stores numbers as eight bytes. */
export function honorsDocumentBytes(value: HonorsRecord): number {
  const stringBytes = (text: string): number =>
    new TextEncoder().encode(text).length + 1;
  const size = (item: unknown): number => {
    if (typeof item === "string") return stringBytes(item);
    if (typeof item === "boolean") return 1;
    if (typeof item === "number") return 8;
    if (Array.isArray(item))
      return item.reduce((total, entry) => total + size(entry), 0);
    if (item && typeof item === "object") {
      return (
        32 +
        Object.entries(item).reduce(
          (total, [key, entry]) => total + stringBytes(key) + size(entry),
          0,
        )
      );
    }
    throw new Error("Unexpected value in honors document.");
  };
  return 128 + size(value);
}

export const HONORS_SIZE_BUDGET = 900 * 1024;

/** Coarse school boundary; section ownership is deliberately a UI concern. */
export function isHonorsAdmin(role?: string, schoolId?: string): boolean {
  return (
    role === "super" ||
    (role === "admin" && (!schoolId?.trim() || schoolId.trim() === "HS"))
  );
}
